import { createPortal } from 'react-dom';
import { Haptic } from '../kit/haptics';

/**
 * Floating "Unsaved changes" pill (UI kit): springs up above the tab bar and the home indicator while a
 * form is dirty. Pair it with a form: `form` names the form the Save button submits.
 * Rendered into <body>, so it stays visible (and fixed to the screen) even inside hidden or transformed panels.
 */
export function SaveBar({ open, saving, onDiscard, form, label = 'Save changes', disabled }: { open: boolean; saving?: boolean; onDiscard: () => void; form: string; label?: string; disabled?: boolean }) {
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 dr-above-dock z-40 flex justify-center dr-safe-px-4 transition-[bottom] duration-300 ease-soft lg:pl-[16.75rem] lg:collapsed:pl-[5.25rem]" aria-hidden={!open}>
      <div
        role="region"
        aria-label="Unsaved changes"
        className={`flex items-center gap-3 rounded-full border border-hairline py-1.5 pr-1.5 pl-5 text-sm dr-glass dr-shadow-lg transition-[translate,opacity] duration-500 ease-spring ${open ? 'pointer-events-auto translate-y-0 opacity-100' : 'translate-y-24 opacity-0'}`}
      >
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          <span className="status status-warning" aria-hidden="true" /> Unsaved changes
        </span>
        <button type="button" tabIndex={open ? 0 : -1} className="btn rounded-full btn-ghost btn-sm" onClick={onDiscard} disabled={saving}>
          Discard
        </button>
        <Haptic radius="999px" disabled={!open || saving || disabled}>
          <button type="submit" form={form} tabIndex={open ? 0 : -1} className="btn rounded-full btn-primary btn-sm" disabled={saving || disabled}>
            {saving ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : null}
            {saving ? 'Saving…' : label}
          </button>
        </Haptic>
      </div>
    </div>,
    document.body,
  );
}
